import { Images } from 'lucide-react';
import type { GalleryBlock } from '@impact/types';
import type { BlockTypeConfig } from '../types';

export const galleryConfig: BlockTypeConfig<GalleryBlock> = {
  type: 'gallery',
  label: 'Gallery',
  description: 'Grid of images with captions, links and multiple layout styles',
  icon: Images,
  category: 'media',
  createDefault: () => ({
    images: [
      { id: crypto.randomUUID(), src: '', alt: '' },
      { id: crypto.randomUUID(), src: '', alt: '' },
      { id: crypto.randomUUID(), src: '', alt: '' },
    ],
    columns: 3,
    gap: 'md',
    aspectRatio: '4/3',
    rounded: true,
    shadow: false,
    showCard: false,
    showCaptions: true,
    captionStyle: 'below',
    lightbox: false,
  }),
  toolbar: [
    {
      kind: 'select',
      path: 'data.columns',
      label: 'Columns',
      options: [
        { value: '2', label: '2' },
        { value: '3', label: '3' },
        { value: '4', label: '4' },
      ],
    },
    {
      kind: 'select',
      path: 'data.gap',
      label: 'Gap',
      options: [
        { value: 'sm', label: 'Tight' },
        { value: 'md', label: 'Medium' },
        { value: 'lg', label: 'Loose' },
      ],
    },
    {
      kind: 'select',
      path: 'data.aspectRatio',
      label: 'Ratio',
      options: [
        { value: 'auto', label: 'Auto' },
        { value: 'video', label: '16:9' },
        { value: 'square', label: '1:1' },
        { value: '4/3', label: '4:3' },
      ],
    },
    { kind: 'variant' },
    { kind: 'divider' },
    { kind: 'more' },
  ],
  sheet: [
    {
      kind: 'group',
      label: 'Layout',
      fields: [
        { kind: 'toggle', path: 'data.rounded', label: 'Rounded corners' },
        { kind: 'toggle', path: 'data.shadow', label: 'Shadow on each cell' },
        { kind: 'toggle', path: 'data.showCard', label: 'Card background' },
      ],
    },
    {
      kind: 'group',
      label: 'Captions',
      fields: [
        { kind: 'toggle', path: 'data.showCaptions', label: 'Show captions' },
        {
          kind: 'select',
          path: 'data.captionStyle',
          label: 'Caption position',
          options: [
            { value: 'below', label: 'Below image' },
            { value: 'overlay', label: 'Overlay on hover' },
          ],
        },
      ],
    },
    {
      kind: 'group',
      label: 'Behaviour',
      fields: [
        { kind: 'toggle', path: 'data.lightbox', label: 'Open in lightbox' },
        { kind: 'toggle', path: 'data.showButton', label: 'Show download button' },
        { kind: 'text', path: 'data.buttonText', label: 'Button text', placeholder: 'Download' },
        {
          kind: 'select',
          path: 'data.buttonStyle',
          label: 'Button style',
          options: [
            { value: 'primary', label: 'Primary' },
            { value: 'secondary', label: 'Secondary' },
            { value: 'outline', label: 'Outline' },
          ],
        },
      ],
    },
  ],
  variants: [
    {
      id: 'default',
      label: 'Grid',
      thumbnail: '/admin/page-editor/variants/gallery-default.svg',
      apply: (d) => ({ ...d, variant: 'default' }),
    },
    {
      id: 'masonry',
      label: 'Masonry',
      thumbnail: '/admin/page-editor/variants/gallery-masonry.svg',
      apply: (d) => ({ ...d, variant: 'masonry', aspectRatio: 'auto' }),
    },
    {
      id: 'mosaic',
      label: 'Mosaic',
      thumbnail: '/admin/page-editor/variants/gallery-mosaic.svg',
      apply: (d) => ({ ...d, variant: 'mosaic' }),
    },
    {
      id: 'marquee',
      label: 'Marquee',
      thumbnail: '/admin/page-editor/variants/gallery-marquee.svg',
      apply: (d) => ({ ...d, variant: 'marquee', aspectRatio: 'square' }),
    },
  ],
  repeatingItems: {
    arrayPath: 'data.images',
    itemFactory: () => ({ id: crypto.randomUUID(), src: '', alt: '' }),
    addLabel: 'Add image',
  },
};
